<template>
  <header
    class="w-full md:h-75 py-16 px-10 md:p-25 md:pt-20 transition-all duration-300"
    :class="[
      headerClasses,
      showHeaderClasses,
      !isPaperShape ? isBookPage ? 'md:pl-25' : 'md:pl-15' : 'md:pl-10',
      {'fixed z-40': isMobile},
      {'shadow-header': isBookPage && !isPaperShape && !isDarkMode && currentScrollPos >= 5},
      {'md:pl-18': isBooksPage}
    ]"
  >
    <div class="flex w-full items-center justify-between anim-opacity" :class="isFocusMode && !isMobile && !showSidebar ? 'opacity-0' : 'opacity-100'">
      <div class="flex items-center" :class="isBooksPage ? 'w-750 max-w-full' : ''">
        <ButtonModule
          v-if="showSidebarToggle"
          class="text-18 px-10 py-6 rounded-6 anim-color-200 opacity-50 anim-opacity text-opacity-60 text-dark-600"
          :class="toggleButtonClasses"
          prefix="icon-menu text-22"
          @click="toggleSidebar"
        />

        <SpaceDropdown v-if="!isBooksPage" :disabled="isBookPage" />

        <ButtonModule
          v-if="isBooksPage"
          prefix="icon-search mr-7"
          :icon-has-margin="false"
          text="search"
          class="px-10 py-7 rounded-6 anim-color-200 text-16 opacity-50 anim-opacity hover:opacity-100"
          :class="[
            isMagenta ? 'text-dark-600' : 'text-app-color',
            isDarkMode ? 'hover:bg-dark-500' : 'hover:bg-menu-hover',
          ]"
          @click="openSearchModal"
        />
      </div>
      <div class="flex items-center">
        <div
          id="menu-items"
          class="flex items-center"
        >
          <div v-if="projectInstructionsActiveFourthStep" class="w-full h-full absolute z-9999 -ml-15" />

          <transition name="fade">
            <ButtonModule
              v-if="isBookPage && currentProject?.isLocked"
              :has-hover="!currentProject.isLocked"
              class="mr-10 px-0 py-0"
              :class="currentProject.isLocked ? 'text-main-color' : 'text-app-color'"
              :prefix="isDocLocked ? 'icon-lock-close' : 'icon-lock-open'"
              @click="lock"
            />
          </transition>

          <Share
            v-if="isBookPage && currentProject"
            id="share-menu"
            :project="currentProject"
            class="md:mr-2"
          />
          <More v-if="isBookPage && currentProject" />

          <UserDropdown v-if="user" class="ml-6 md:ml-12" />
        </div>
      </div>
    </div>
    <SearchBar />
  </header>
</template>

<script>
import SearchBar from '@/components/shared/SearchBar'
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import Share from '@/components/header/Share'
import More from '@/components/header/More'
import UserDropdown from '@/components/header/UserDropdown'
import SpaceDropdown from '@/components/header/SpaceDropdown'

import { mapGetters, mapState, mapActions, mapMutations } from 'vuex'

export default {
  components: {
    SearchBar,
    ButtonModule,
    Share,
    More,
    UserDropdown,
    SpaceDropdown
  },

  props: {
    isUpgradeToProBar: Boolean,
    isPoseidonTheme: Boolean
  },

  emits: ['toggleSidebar'],

  data () {
    return {
      currentScrollPos: 0,
      isDocLocked: true
    }
  },

  computed: {
    ...mapGetters(['isMobile', 'isDarkMode', 'isTeamAccount', 'isMagenta', 'isPaperShape']),

    ...mapState({
      settings: state => state.settings.settings,
      showSidebar: state => state.settings.showSidebar,
      user: state => state.profile.user,
      showHeader: state => state.settings.showHeader,
      isFocusMode: state => state.settings.isFocusMode,
      currentProject: state => state.project.currentProject,
      projectInstructionsActiveStep: state => state.profile.activeStep,
      isProjectInstructionsAccepted: state => state.profile.user.profile.showProjectOnboarding
    }),

    isBookPage () {
      return this.$route.name === 'Book' || this.$route.name === 'TeamBook' || this.$route.name === 'Headquarters'
    },

    isBooksPage () {
      return this.$route.name === 'Books' || this.$route.name === 'TeamBooks'
    },

    projectInstructionsActiveFourthStep () {
      return !this.isProjectInstructionsAccepted && this.projectInstructionsActiveStep === 4
    },

    showSidebarToggle () {
      return (((this.isBookPage || this.isToolPage) && !this.showSidebar) || this.isMobile) && !this.isBooksPage
    },

    toggleButtonClasses () {
      const prefix = this.showSidebar ? '' : 'hover:'
      const textColor = this.isMagenta ? 'text-dark-600' : 'text-app-color'
      return `${this.isDarkMode.value ? `${prefix}bg-dark-500 ${prefix}opacity-100` : `${prefix}bg-menu-hover ${prefix}opacity-100`} ${textColor}`
    },

    isToolPage () {
      return ['TaskBoard', 'TaskBoardTeam', 'WeekPlanner', 'WeekPlannerTeam', 'Discussions'].includes(this.$route.name)
    },

    headerClasses () {
      let classes = ''
      let bg = ''

      if (this.isProjectInstructionsAccepted) {
        if (this.showSidebar && this.isMobile) {
          if (this.isBooksPage) classes += ' left-268 '
          else if (this.isBookPage) classes += ' left-323 '
          else classes += ' left-95 '
        } else {
          classes += ' left-0 md:left-20 w-full '
        }

        if (this.isPoseidonTheme) {
          if (this.isBookPage) {
            classes += this.isPaperShape ? ' w-full max-w-1100 mx-auto' : ' w-full'
            bg = this.isPaperShape ? ' bg-book' : ' bg-primary'
          }
        } else {
          if (this.isPaperShape) {
            const maxWidth = this.isBookPage ? 'max-w-1100' : 'max-w-1600'
            classes += this.isBookPage || this.isToolPage ? ` ${maxWidth} mx-auto w-full` : ''
            bg = this.isBookPage || this.isToolPage ? ' bg-book' : ''
          } else {
            bg = this.isToolPage ? ' bg-canvas' : this.isBooksPage ? '' : ' bg-primary'
          }
        }
      }

      return classes + bg
    },

    showHeaderClasses () {
      if (this.isUpgradeToProBar) {
        return this.showHeader ? 'top-150' : 'top-90'
      } else {
        return this.showHeader ? 'top-0' : '-top-57'
      }
    }
  },

  mounted () {
    document.addEventListener('scroll', this.handleSCroll)
    document.addEventListener('keydown', this.focusSearch)
  },

  beforeUnmount () {
    document.removeEventListener('scroll', this.handleSCroll)
    document.removeEventListener('keydown', this.focusSearch)
  },

  methods: {
    ...mapActions(['editProject']),
    ...mapMutations(['loading', 'setCurrentProject', 'toggleSearch']),

    focusSearch (event) {
      if (event.ctrlKey || event.metaKey) {
        if (event.keyCode === 75) {
          event.preventDefault()
          this.openSearchModal()
        }
      }
    },

    handleSCroll () {
      this.currentScrollPos = window.pageYOffset
    },

    toggleSidebar () {
      this.$emit('toggleSidebar')
    },

    lock () {
      this.isDocLocked = false

      setTimeout(() => {
        this.loading(true)

        this.editProject({
          id: this.currentProject.id,
          data: {
            ...this.currentProject,
            isLocked: !this.currentProject.isLocked
          }
        }).then(() => {
          this.setCurrentProject({
            ...this.currentProject,
            isLocked: !this.currentProject.isLocked
          })
        }).finally(() => {
          this.isDocLocked = true
        })
      }, 500)
    },

    openSearchModal () {
      this.toggleSearch(true)
    }
  }
}
</script>
